import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const ContactInfo = () => {
  const contactMethods = [
    {
      icon: "Phone",
      title: "Téléphone",
      primary: "+243 81 888 3531",
      secondary: "+243 YYY YYY YYY",
      description: "Disponible Lun-Ven 8h-18h",
      action: "Appeler maintenant",
      href: "tel:+243818883531"
    },
    {
      icon: "Mail",
      title: "Email",
      primary: "info@orazek.com",
      secondary: "contact@orazek.com",
      description: "Réponse sous 24h",
      action: "Envoyer un email",
      href: "mailto:info@orazek.com"
    },
    {
      icon: "MessageCircle",
      title: "WhatsApp",
      primary: "+243 999 916 801",
      secondary: "Chat en direct",
      description: "Réponse immédiate",
      action: "Ouvrir WhatsApp",
      href: "https://wa.me/243999916801"
    }
  ];

  const offices = [
    {
      city: "Kinshasa",
      address: "Avenue de la Libération, Gombe",
      region: "Kinshasa, RDC",
      hours: "Lun-Ven: 8h00 - 18h00\nSam: 9h00 - 13h00",
      coordinates: { lat: -4.3276, lng: 15.3136 }
    },
    {
      city: "Lubumbashi",
      address: "Avenue Mobutu, Centre-ville",
      region: "Haut-Katanga, RDC",
      hours: "Lun-Ven: 8h00 - 18h00\nSam: 9h00 - 13h00",
      coordinates: { lat: -11.6645, lng: 27.4794 }
    }
  ];

  return (
    <section className="py-20 lg:py-28 bg-slate-100 dark:bg-slate-900 border-y border-slate-200 dark:border-white/5 relative overflow-hidden">

      {/* Background */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-slate-800 via-slate-900 to-black opacity-0 dark:opacity-80" />
      <div className="absolute inset-0 opacity-0 dark:opacity-10 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] mix-blend-overlay"></div>

      <div className="w-full max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-16 relative z-10">

        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full mb-6">
            <Icon name="Phone" size={16} className="text-amber-400" />
            <span className="text-sm font-medium text-slate-600 dark:text-slate-300">Nos Coordonnées</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-bold text-slate-900 dark:text-white mb-6">
            Plusieurs Moyens de <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-amber-400">
              Nous Contacter
            </span>
          </h2>
          <p className="text-xl text-slate-600 dark:text-slate-400 max-w-3xl mx-auto leading-relaxed">
            Choisissez le canal de communication qui vous convient le mieux
          </p>
        </motion.div>

        {/* Contact Methods Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {contactMethods?.map((method, index) => (
            <motion.a
              key={index}
              href={method?.href}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.02, translateY: -5 }}
              className="group bg-white/80 dark:bg-slate-800/40 backdrop-blur-xl border border-slate-200 dark:border-white/10 rounded-3xl p-10 shadow-2xl cursor-pointer transition-all duration-300 hover:bg-slate-100 dark:hover:bg-slate-800/60 hover:border-blue-500/30 block"
            >
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center mb-8 shadow-lg group-hover:scale-110 transition-transform duration-300">
                <Icon name={method?.icon} size={30} className="text-white" />
              </div>

              <div className="text-xs font-bold text-blue-400 uppercase tracking-widest mb-3">
                {method?.title}
              </div>

              <div className="space-y-2 mb-4">
                <p className="text-xl md:text-2xl font-bold text-slate-900 dark:text-white group-hover:text-amber-400 transition-colors">
                  {method?.primary}
                </p>
                <p className="text-base text-slate-500 dark:text-slate-400">
                  {method?.secondary}
                </p>
              </div>

              <p className="text-sm text-slate-500 dark:text-slate-400 mb-8">
                {method?.description}
              </p>

              <div className="flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-white group-hover:text-amber-400 group-hover:gap-3 transition-all duration-300">
                {method?.action}
                <Icon name="ArrowRight" size={18} />
              </div>
            </motion.a>
          ))}
        </div>

        {/* Offices Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-12 flex items-center justify-center gap-4">
            <span className="h-px w-12 bg-slate-200 dark:bg-slate-700"></span>
            Nos Bureaux
            <span className="h-px w-12 bg-slate-200 dark:bg-slate-700"></span>
          </h3>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {offices?.map((office, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15 }}
              className="bg-white/80 dark:bg-slate-800/40 backdrop-blur-xl border border-slate-200 dark:border-white/10 rounded-3xl overflow-hidden shadow-2xl hover:border-blue-500/30 transition-all duration-300"
            >
              <div className="h-56 lg:h-64 w-full overflow-hidden">
                <iframe
                  width="100%"
                  height="100%"
                  loading="lazy"
                  title={`Bureau ORAZEK ${office?.city}`}
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://www.google.com/maps?q=${office?.coordinates?.lat},${office?.coordinates?.lng}&z=14&output=embed`}
                  className="border-0 opacity-80 hover:opacity-100 transition-opacity duration-300"
                ></iframe>
              </div>

              <div className="p-8 md:p-10">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center flex-shrink-0 shadow-lg">
                    <Icon name="MapPin" size={22} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                      Bureau de {office?.city}
                    </h3>
                    <p className="text-base text-slate-600 dark:text-slate-400 mb-1">
                      {office?.address}
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {office?.region}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-5 bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-white/5 rounded-xl">
                  <Icon name="Clock" size={20} className="text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 dark:text-white mb-1">
                      Horaires d'ouverture
                    </p>
                    <p className="text-sm text-slate-600 dark:text-slate-400 whitespace-pre-line">
                      {office?.hours}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ContactInfo;
